import { observer } from "mobx-react-lite";
import { useCallback, useContext, useRef, useState } from "react";
import { useFormState } from "react-final-form";

import {
  NoDataTile,
  ScrollablePane,
  SelectionMode,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { PagingOptions } from "@libs/api/dtos/index.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { ContactFetcher } from "@ui-components/ContactFetcher.tsx";
import { InfiniteScrollList } from "@ui-components/InfiniteScrollList/InfiniteScrollList.tsx";
import { Selection } from "@ui-components/ShimmeredDetailsList/Selection.ts";

import { StatementItemsContext } from "../context/StatementItemsContext.ts";
import { StatementGenerateFormValues } from "./StatementGenerateModal.type.ts";

export const StatementSecondColumn = observer(() => {
  const {
    searchAccountIds,
    setSelectedIds,
    setEmailedStatementsNumber,
    total
  } = useContext(StatementItemsContext);

  const { values } = useFormState<StatementGenerateFormValues>();

  const { practice } = useStores();

  const [, setRefresh] = useState(true);
  // Track the very first page load only
  const isFirstPageLoad = useRef(true);
  const [selection] = useState(
    new Selection({
      onSelectionChanged: () => selectionChangedHandler(),
      onItemsChanged: () => itemsChangedHandler()
    })
  );

  // Keep track of all items that have been loaded
  const allLoadedItems = useRef<string[]>([]);
  const selectedKeys = useRef<string[]>([]);

  const selectionChangedHandler = () => {
    const previousSelectedKeys = selectedKeys.current;
    const keysInSelection = (selection.getItems() as { id: string }[]).map(
      x => x.id
    );

    // Update our record of all loaded items
    for (const key of keysInSelection) {
      if (!allLoadedItems.current.includes(key)) {
        allLoadedItems.current.push(key);
      }
    }

    const currentSelectedKeys = (
      selection.getSelection() as { id: string }[]
    ).map(x => x.id);

    const newSelectedKeys = [
      ...currentSelectedKeys,
      ...previousSelectedKeys.filter(
        (
          key // keep previously selected keys if
        ) =>
          !keysInSelection.includes(key) || // not in current selection
          (keysInSelection.includes(key) && currentSelectedKeys.includes(key)) // or in current selection and is selected
      )
    ];

    const newUniqueKeys = [...new Set(newSelectedKeys)];
    selectedKeys.current = newUniqueKeys;

    // Only count selected IDs that exist in the known items that have been loaded
    const validSelectedIds = newUniqueKeys.filter(id =>
      allLoadedItems.current.includes(id)
    );

    setSelectedIds(validSelectedIds);
    setRefresh(prevValue => !prevValue);

    setEmailedStatementsNumber(
      validSelectedIds.reduce(
        (count, id) =>
          practice.contactsMap.get(id)?.billingEmail ? count + 1 : count,
        0
      )
    );
  };

  const itemsChangedHandler = () => {
    const items = (selection.getItems() as { id: string }[]).map(x => x.id);

    // Update all loaded items tracking
    for (const id of items) {
      if (!allLoadedItems.current.includes(id)) {
        allLoadedItems.current.push(id);
      }
    }

    // If it's the first page load (not just form value changes), select all items
    if (isFirstPageLoad.current && items.length > 0) {
      isFirstPageLoad.current = false;

      // Select all items on first load
      for (let i = 0; i < items.length; i++) {
        selection.setIndexSelected(i, true, false);
      }

      // Update selectedKeys with all item IDs
      selectedKeys.current = [...items];

      // Only pass the current valid items to the context
      setSelectedIds(selectedKeys.current);

      // Update emailed statements number
      setEmailedStatementsNumber(
        selectedKeys.current.reduce(
          (count, id) =>
            practice.contactsMap.get(id)?.billingEmail ? count + 1 : count,
          0
        )
      );

      setRefresh(prevValue => !prevValue);
      return;
    }
    // For subsequent loads, keep existing behavior
    for (const id of items) {
      const index = items.findIndex(x => x === id);
      selection.setIndexSelected(
        index,
        selectedKeys.current.includes(id),
        false
      );
    }

    // After handling selection, ensure we're only tracking valid items
    const validSelectedIds = selectedKeys.current.filter(id =>
      allLoadedItems.current.includes(id)
    );

    setSelectedIds(validSelectedIds);
  };

  const theme = useTheme();

  const search = useCallback(
    async (query?: PagingOptions) => {
      // When search params change, reset our tracking of all loaded items
      if (!query || query.skip === 0 || query.skip === undefined) {
        allLoadedItems.current = [];
      }
      return await searchAccountIds(
        {
          gapDays: values.gapDays,
          endDate: values.endDate,
          owingOver: values.owingOver,
          creditOver: values.creditOver,
          accountContactIds: values.accountContactIds,
          contactType: values.contactType
        },
        query
      );
    },
    [
      searchAccountIds,
      values.accountContactIds,
      values.creditOver,
      values.endDate,
      values.gapDays,
      values.owingOver,
      values.contactType
    ]
  );

  const hideRecipientsHeader = total === undefined || total === 0;

  const columns = [
    {
      name: "Recipients",
      key: "date",
      minWidth: 79,
      maxWidth: 79,
      onRender: (props: { id: string }) => (
        <ContactFetcher
          contactId={props.id}
          options={{ includeContactPreferences: true }}
        >
          {contact => <Text>{contact.name}</Text>}
        </ContactFetcher>
      )
    }
  ];

  return (
    <div style={{ position: "relative", flexGrow: 1 }}>
      <ScrollablePane
        styles={{
          root: {
            pointerEvents: "none",
            "& .bp-list-wrapper": { pointerEvents: "auto" }
          }
        }}
      >
        <InfiniteScrollList
          setKey="statements-recipients-list"
          getItems={search}
          detailsListStyles={{
            root: {
              ".ms-DetailsRow-cell": {
                alignSelf: "center"
              },
              ".is-selected:hover": {
                backgroundColor: theme.palette.neutralLight,
                ".ms-DetailsRow-cell span": {
                  color: theme.palette.neutralDark
                }
              },
              ".ms-DetailsHeader": {
                display: hideRecipientsHeader ? "none" : "block"
              }
            }
          }}
          columns={columns}
          selection={selection}
          selectionMode={SelectionMode.multiple}
          displayDefaultCheckBox
          onRenderNoResults={() => (
            <NoDataTile
              showBoxShadow={false}
              textProps={{ text: "No statements" }}
              linkProps={{ hidden: true }}
            />
          )}
        />
      </ScrollablePane>
    </div>
  );
});
